'use client'
import { useLayoutEffect, useState, useRef, ReactElement, MouseEvent, PropsWithChildren } from 'react'

type Props = PropsWithChildren<({
  content: ReactElement | string
})>

export default function Tooltip({ content, children }: Props) {
  const offset = 15

  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [mouseAbs, setMouseAbs] = useState({ cx: 0, cy: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => setIsVisible(true)
  const handleMouseLeave = () => setIsVisible(false)

  const handleMouseMove = (e: MouseEvent) => {
    const triggerRect = triggerRef.current?.getBoundingClientRect()
    if (!triggerRect) return

    // Store last absolute mouse coords so we can adjust the tooltip
    // position once tooltip size is known (else, tooltip could be
    // calculated using (0, 0) coords due to hydration latency).
    setMouseAbs({ cx: e.clientX, cy: e.clientY })

    setPosition({
      x: e.clientX - triggerRect.left + offset, // Optimist -> right/below.
      y: e.clientY - triggerRect.top + offset,
    })
  }

  /* ---------- Second-pass adjustment ---------- */
  useLayoutEffect(() => {
    if (!isVisible || !tooltipRef.current || !triggerRef.current) return
    const { width: ttW, height: ttH } = tooltipRef.current.getBoundingClientRect()
    const { left: trgL, top: trgT } = triggerRef.current.getBoundingClientRect()

    let x, y

    // Choose the lesser evil if there's no way to not overflow.
    // Calculate available space on each side.
    const spaceRight = window.innerWidth - mouseAbs.cx - offset
    const spaceLeft = mouseAbs.cx - offset
    const spaceBottom = window.innerHeight - mouseAbs.cy - offset
    const spaceTop = mouseAbs.cy - offset

    // Determine x position: choose side with more less overflow.
    if (ttW <= spaceRight) {
      // Fully fits on the right.
      x = mouseAbs.cx - trgL + offset
    } else if (ttW <= spaceLeft) {
      // Doesn't fully fit on the right, but fully fits on the left.
      x = mouseAbs.cx - trgL - ttW - offset
    } else {
      // Doesn't fully fit on either side. Choose side with more space.
      if (spaceRight >= spaceLeft) {
        x = Math.min(
          mouseAbs.cx - trgL + offset,
          window.innerWidth - trgL - ttW - offset, // Clamp to right edge.
        )
      } else {
        x = Math.max(
          mouseAbs.cx - trgL - ttW - offset, 
          -trgL + offset,  // Clamp to left edge.
        )
      }
    }

    // Determine y position: choose side with more less overflow.
    if (ttH <= spaceBottom) {
      // Fully fits below.
      y = mouseAbs.cy - trgT + offset
    } else if (ttH <= spaceTop) {
      // Doesn't fully fit below, but fully fits above.
      y = mouseAbs.cy - trgT - ttH - offset
    } else {
      // Doesn't fully fit on either side. Choose side with more space.
      if (spaceBottom >= spaceTop) {
        y = Math.min(
          mouseAbs.cy - trgT + offset,
          window.innerHeight - trgT - ttH - offset, // Clamp to bottom edge.
        )
      } else {
        y = Math.max(
          mouseAbs.cy - trgT - ttH - offset,
          trgT + offset, // Clamp to top edge.
        )
      }
    }

    setPosition(prev => (prev.x === x && prev.y === y) ? prev: { x, y })
  }, [isVisible, mouseAbs]) // Runs as soon as tooltip is mounted or mouse moves.

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="absolute z-50 pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
        >
          {content}
        </div>
      )}
    </div>
  )
}
